"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.processSocialMutations = processSocialMutations;
var _rdflib = require("rdflib");
var _solidUi = require("solid-ui");
var _idNodeFactory = require("../shared/idNodeFactory");
var _rdfMutationHelpers = require("../shared/rdfMutationHelpers");
var _texts = require("../../texts");
var _helpers = require("./helpers");
var _selectors = require("./selectors");
var _rdfList = require("../shared/rdfList");
/* The code below for handling reordering were generated by AI Model: GPT-5.3-Codex. */
/* Prompt: can you implement on SocialEditDialog add the bentoIcon as the first item 
on the row, you should be able to drag this button around and it should allow you 
to change the order of the rows and that should change the order in the mutations 
that happen for this data. */

const SOCIAL_ONTOLOGY_NS = 'https://solidos.github.io/profile-pane/src/ontology/socialMedia.ttl#';
function ensureSocialPrefix(store) {
  const anyStore = store;
  if (typeof anyStore.setPrefixForURI === 'function') {
    anyStore.setPrefixForURI('soc', SOCIAL_ONTOLOGY_NS);
    return;
  }
  if (!anyStore.namespaces) {
    anyStore.namespaces = {};
  }
  anyStore.namespaces.soc = SOCIAL_ONTOLOGY_NS;
}
function isSocialPatchDebugEnabled() {
  const debugFlag = globalThis?.__PROFILE_PANE_SOCIAL_DEBUG__;
  return debugFlag === true || debugFlag === '1' || debugFlag === 'true';
}
function statementDebugValue(term) {
  if (!term) return '';
  if (typeof term.toNT === 'function') return term.toNT();
  if (typeof term.value === 'string') return term.value;
  return String(term);
}
function statementToDebugObject(statement) {
  return {
    subject: statementDebugValue(statement?.subject),
    predicate: statementDebugValue(statement?.predicate),
    object: statementDebugValue(statement?.object),
    graph: statementDebugValue(statement?.why)
  };
}
function toObjectNode(value) {
  const normalized = (value || '').trim();
  if (!normalized) return null;
  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    return (0, _rdflib.sym)(normalized);
  }
  return (0, _rdflib.literal)(normalized);
}
function normalizeValue(value) {
  if (typeof value === 'string') return value.trim();
  if (value === null || value === undefined) return '';
  return String(value).trim();
}
function toAccountNameFromHomepage(homepage, profilePrefix) {
  const homepageValue = normalizeValue(homepage);
  if (!homepageValue) return '';
  const prefixValue = normalizeValue(profilePrefix);
  if (prefixValue) {
    let trimmed = homepageValue;
    const lowerPrefix = prefixValue.toLowerCase();
    while (trimmed.toLowerCase().startsWith(lowerPrefix)) {
      trimmed = trimmed.slice(prefixValue.length);
    }
    if (trimmed) return trimmed;
    try {
      const homepageUrl = new URL(homepageValue);
      const prefixUrl = new URL(prefixValue);
      const normalizeHost = host => host.toLowerCase().replace(/^www\./, '');
      const homepageHost = normalizeHost(homepageUrl.hostname);
      const prefixHost = normalizeHost(prefixUrl.hostname);
      const prefixPath = (prefixUrl.pathname || '/').replace(/\/+$/, '');
      const homepagePath = homepageUrl.pathname || '/';
      if (homepageHost === prefixHost && homepagePath.startsWith(prefixPath)) {
        const remainder = homepagePath.slice(prefixPath.length).replace(/^\/+/, '');
        if (remainder) return remainder;
      }
    } catch {
      // Ignore URL parsing issues and continue to generic normalization.
    }
  }
  return homepageValue.replace(/^https?:\/\//i, '').replace(/^www\./i, '');
}
function buildSocialStatements(doc, node, row, accountOption) {
  const inserts = [];
  if (accountOption?.classUri) {
    inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), (0, _rdflib.sym)(accountOption.classUri), doc));
  }
  const normalizedName = normalizeValue(row.name);
  const isOther = normalizedName.toLowerCase() === 'other' || accountOption?.classUri?.endsWith('#OtherAccount');
  if (isOther) {
    const homepageNode = toObjectNode(row.homepage);
    const iconNode = toObjectNode(row.icon);
    if (homepageNode) inserts.push((0, _rdflib.st)(node, _solidUi.ns.foaf('homepage'), homepageNode, doc));
    if (iconNode) inserts.push((0, _rdflib.st)(node, _solidUi.ns.foaf('icon'), iconNode, doc));
    if (normalizedName && normalizedName.toLowerCase() !== 'other') {
      inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdfs('label'), (0, _rdflib.literal)(normalizedName), doc));
    }
    return inserts;
  }
  const accountPrefix = accountOption?.userProfilePrefix || accountOption?.homepage;
  const accountName = toAccountNameFromHomepage(normalizeValue(row.homepage), accountPrefix);
  if (accountName) {
    inserts.push((0, _rdflib.st)(node, _solidUi.ns.foaf('accountName'), (0, _rdflib.literal)(accountName), doc));
  }
  return inserts;
}
function statementKey(statement) {
  return `${statement.subject?.toNT?.() || statement.subject?.value} ${statement.predicate?.toNT?.() || statement.predicate?.value} ${statement.object?.toNT?.() || statement.object?.value} ${statement.why?.toNT?.() || statement.why?.value}`;
}
function uniqueStatements(statements) {
  return Array.from(new Map((statements || []).map(statement => [statementKey(statement), statement])).values());
}
function buildRdfListStatements(doc, items) {
  if (!items.length) {
    return {
      head: _solidUi.ns.rdf('nil'),
      statements: []
    };
  }
  const listNodes = items.map(() => (0, _rdflib.blankNode)());
  const statements = [];
  items.forEach((item, index) => {
    const current = listNodes[index];
    const next = listNodes[index + 1] || _solidUi.ns.rdf('nil');
    statements.push((0, _rdflib.st)(current, _solidUi.ns.rdf('first'), item, doc));
    statements.push((0, _rdflib.st)(current, _solidUi.ns.rdf('rest'), next, doc));
  });
  return {
    head: listNodes[0],
    statements
  };
}
function isPatchFailure(message) {
  const text = (message || '').toLowerCase();
  return text.includes(' on patch ') || text.includes('web error: 400') || text.includes('web error: 405') || text.includes('web error: 501');
}
function sanitizePatchStatements(store, deletions, insertions) {
  const safeDeletions = Array.from(new Map((deletions || []).filter(statement => {
    if (!statement || !statement.subject || !statement.predicate || !statement.object) return false;
    if (store.holds(statement.subject, statement.predicate, statement.object, statement.why)) return true;
    return store.statementsMatching(statement.subject, statement.predicate, statement.object, statement.why).length > 0;
  }).map(statement => [statementKey(statement), statement])).values());
  const safeInsertions = Array.from(new Map((insertions || []).filter(statement => Boolean(statement && statement.subject && statement.predicate && statement.object)).map(statement => [statementKey(statement), statement])).values());
  return {
    safeDeletions,
    safeInsertions
  };
}
async function runPutFallback(store, doc, deletions, insertions) {
  const updater = store.updater;
  const fetcher = store.fetcher;
  if (!updater || typeof updater.serialize !== 'function' || !fetcher || typeof fetcher.webOperation !== 'function') {
    throw new Error('Social updates are not supported by this store updater.');
  }
  const currentStatements = store.statementsMatching(undefined, undefined, undefined, doc).slice();
  const deletionKeys = new Set((deletions || []).map(statement => statementKey(statement)));
  const nextStatements = currentStatements.filter(statement => !deletionKeys.has(statementKey(statement))).concat(insertions || []);
  const contentType = 'text/turtle';
  const body = updater.serialize(doc.value, nextStatements, contentType);
  const response = await fetcher.webOperation('PUT', doc.value, {
    noMeta: true,
    contentType,
    body
  });
  if (!response || response.ok !== true) {
    const status = response?.status || 'unknown';
    throw new Error(`Web error: ${status} on PUT of <${doc.value}>`);
  }
  store.remove(deletions);
  insertions.forEach(statement => {
    store.add(statement.subject, statement.predicate, statement.object, statement.why);
  });
}
async function applySocialPatchWithFallback(store, doc, deletions, insertions) {
  if (!store.updater) {
    throw new Error(_texts.updaterUnsupportedStoreErrorMessageText);
  }
  const {
    safeDeletions,
    safeInsertions
  } = sanitizePatchStatements(store, deletions, insertions);
  if (safeDeletions.length === 0 && safeInsertions.length === 0) {
    return;
  }
  try {
    await (0, _rdfMutationHelpers.applyUpdaterPatch)(store, safeDeletions, safeInsertions);
    return;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (!isPatchFailure(message)) throw error;
  }
  await runPutFallback(store, doc, safeDeletions, safeInsertions);
}
function collectListChainNodes(store, listHead, doc) {
  if (!store.any(listHead, _solidUi.ns.rdf('first'), null, doc)) return [];
  const visited = new Set();
  const nodes = [];
  let current = listHead;
  while (current) {
    const key = `${current.termType}:${current.value}`;
    if (visited.has(key)) break;
    visited.add(key);
    nodes.push(current);
    const rest = store.any(current, _solidUi.ns.rdf('rest'), null, doc);
    if (!rest || rest.termType === 'NamedNode' && rest.value === _solidUi.ns.rdf('nil').value) {
      break;
    }
    current = rest;
  }
  return nodes;
}
function mergeSocialOps(existingRows, socialOps) {
  const removeKeys = new Set(socialOps.remove.map(row => normalizeValue(row.entryNode)).filter(Boolean));
  const updateByKey = new Map(socialOps.update.map(row => [normalizeValue(row.entryNode), row]).filter(([key]) => Boolean(key)));
  const mergedExisting = existingRows.filter(row => !removeKeys.has(normalizeValue(row.entryNode))).map(row => {
    const key = normalizeValue(row.entryNode);
    return updateByKey.get(key) || row;
  });
  const creates = socialOps.create.filter(row => {
    return Boolean(normalizeValue(row.name) || normalizeValue(row.homepage) || normalizeValue(row.icon));
  });
  return [...mergedExisting, ...creates];
}
function rowsFromOrderedInput(orderedRows) {
  return (orderedRows || []).filter(row => row.status !== 'deleted').filter(row => Boolean(normalizeValue(row.name) || normalizeValue(row.homepage) || normalizeValue(row.icon) || normalizeValue(row.entryNode))).map(row => ({
    name: normalizeValue(row.name),
    icon: normalizeValue(row.icon),
    homepage: normalizeValue(row.homepage),
    entryNode: normalizeValue(row.entryNode),
    status: row.status
  }));
}
async function mutateSocialEntries(store, subject, socialOps, orderedRows) {
  ensureSocialPrefix(store);
  const doc = subject.doc();
  const accountOptions = (0, _helpers.getSocialAccountOptions)(store);
  const optionForRow = row => (0, _helpers.findSocialAccountOption)(accountOptions, normalizeValue(row.name));
  const existingRows = (0, _selectors.presentSocial)(subject, store).accounts.map(account => ({
    name: normalizeValue(account.name),
    icon: normalizeValue(account.icon),
    homepage: normalizeValue(account.homepage),
    entryNode: account.entryNode.value,
    status: 'existing'
  }));
  const nextRows = orderedRows && orderedRows.length ? rowsFromOrderedInput(orderedRows) : mergeSocialOps(existingRows, socialOps);
  const accountObjects = store.each(subject, _solidUi.ns.foaf('account'), null, doc);
  const existingListNodes = uniqueStatements(accountObjects.flatMap(objectNode => {
    return collectListChainNodes(store, objectNode, doc).flatMap(node => store.statementsMatching(node, null, null, doc));
  }));
  const existingAccountNodes = Array.from(new Map(accountObjects.flatMap(objectNode => (0, _rdfList.expandRdfList)(store, objectNode)).filter(node => node.termType === 'NamedNode').map(node => [`${node.termType}:${node.value}`, node])).values());
  const rowEntryNodes = nextRows.map(row => {
    if (normalizeValue(row.entryNode)) {
      const existing = (0, _rdfMutationHelpers.findExistingNode)(existingAccountNodes, row.entryNode);
      if (existing && existing.termType === 'NamedNode') return existing;
    }
    return (0, _idNodeFactory.createIdNode)(doc);
  });
  const insertions = [];
  if (rowEntryNodes.length > 0) {
    const rdfList = buildRdfListStatements(doc, rowEntryNodes);
    insertions.push((0, _rdflib.st)(subject, _solidUi.ns.foaf('account'), rdfList.head, doc));
    insertions.push(...rdfList.statements);
  }
  nextRows.forEach((row, index) => {
    insertions.push(...buildSocialStatements(doc, rowEntryNodes[index], row, optionForRow(row)));
  });
  const deletions = uniqueStatements([...store.statementsMatching(subject, _solidUi.ns.foaf('account'), null, doc), ...existingListNodes, ...existingAccountNodes.flatMap(node => (0, _rdfMutationHelpers.collectNodeStatements)(store, node, doc))]);
  if (isSocialPatchDebugEnabled()) {
    console.log('[social-mutations] patch payload', {
      subject: subject.value,
      doc: doc.value,
      nextRows,
      deletionsCount: deletions.length,
      insertionsCount: insertions.length,
      deletions: deletions.map(statementToDebugObject),
      insertions: uniqueStatements(insertions).map(statementToDebugObject)
    });
  }
  await applySocialPatchWithFallback(store, doc, deletions, uniqueStatements(insertions));
}
async function processSocialMutations(store, subject, mutationPlan, orderedRows) {
  try {
    await mutateSocialEntries(store, subject, mutationPlan, orderedRows);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`${_texts.saveSocialUpdatesFailedPrefixText} ${message}`);
  }
}